import { useState, useEffect } from 'react';
import { apiRequest, getImageUrl } from '../../utils/api';

const CategoryFormModal = ({ isOpen, onClose, category, organizationId, onSuccess, categories = [] }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        parent_id: null,
        sort_order: 0,
        status: 'active',
        time_based_active: false,
        start_time: '',
        end_time: '',
        start_date: '',
        end_date: '',
        active_days: [],
        availability_schedule: null,
        schedule_mode: 'simple', // 'simple' or 'advanced'
        display_type: 'grid_double', // 'grid_single', 'grid_double', 'list'
        campaigns_menu_ignore_schedule: false
    });
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Hierarchical category options
    const getCategoryOptions = (cats, level = 0, excludeId = null) => {
        let options = [];
        cats.filter(c => !c.parent_id && c.id !== excludeId).forEach(cat => {
            options.push({ id: cat.id, name: cat.name, level });
            // Add children
            const children = cats.filter(c => c.parent_id === cat.id && c.id !== excludeId);
            children.forEach(child => {
                options.push({ id: child.id, name: child.name, level: level + 1 });
                // Add grandchildren
                const grandchildren = cats.filter(c => c.parent_id === child.id && c.id !== excludeId);
                grandchildren.forEach(gc => {
                    options.push({ id: gc.id, name: gc.name, level: level + 2 });
                });
            });
        });
        return options;
    };

    const categoryOptions = getCategoryOptions(categories, 0, category?.id);

    const days = [
        { value: 'monday', label: 'Pazartesi' },
        { value: 'tuesday', label: 'Salı' },
        { value: 'wednesday', label: 'Çarşamba' },
        { value: 'thursday', label: 'Perşembe' },
        { value: 'friday', label: 'Cuma' },
        { value: 'saturday', label: 'Cumartesi' },
        { value: 'sunday', label: 'Pazar' }
    ];

    useEffect(() => {
        if (category) {
            const parsedActiveDays = category.active_days 
                ? (Array.isArray(category.active_days) ? category.active_days : JSON.parse(category.active_days))
                : [];
            const parsedSchedule = category.availability_schedule
                ? (typeof category.availability_schedule === 'string' ? JSON.parse(category.availability_schedule) : category.availability_schedule)
                : null;
            
            setFormData({
                name: category.name || '',
                description: category.description || '',
                parent_id: category.parent_id || null,
                sort_order: category.sort_order || 0,
                status: category.status || 'active',
                time_based_active: category.time_based_active || false,
                start_time: category.start_time || '',
                end_time: category.end_time || '',
                start_date: category.start_date || '',
                end_date: category.end_date || '',
                active_days: parsedActiveDays,
                availability_schedule: parsedSchedule,
                schedule_mode: parsedSchedule ? 'advanced' : 'simple',
                display_type: category.display_type || 'grid_double',
                campaigns_menu_ignore_schedule: category.campaigns_menu_ignore_schedule || false
            });
            setImagePreview(category.image || null);
        } else {
            resetForm();
        }
    }, [category, isOpen]);

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            parent_id: null,
            sort_order: 0,
            status: 'active',
            time_based_active: false,
            start_time: '',
            end_time: '',
            start_date: '',
            end_date: '',
            active_days: [],
            availability_schedule: null,
            schedule_mode: 'simple',
            display_type: 'grid_double',
            campaigns_menu_ignore_schedule: false
        });
        setImage(null);
        setImagePreview(null);
        setError(null);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDayToggle = (day) => {
        setFormData(prev => ({
            ...prev,
            active_days: prev.active_days.includes(day)
                ? prev.active_days.filter(d => d !== day)
                : [...prev.active_days, day]
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setError('Geçersiz dosya formatı. Sadece JPG, PNG, GIF veya WebP formatları desteklenir.');
            e.target.value = ''; // Reset input
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setError('Dosya boyutu çok büyük. Maksimum 5MB olmalıdır.');
            e.target.value = ''; // Reset input
            return;
        }

        setError(null);
        setImage(file);
        const reader = new FileReader();
        reader.onerror = () => {
            setError('Dosya okunurken bir hata oluştu.');
        };
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            
            // Explicitly handle display_type first
            const displayTypeValue = formData.display_type || 'grid_double';
            formDataToSend.append('display_type', displayTypeValue);
            console.log('🔍 Explicitly adding display_type to FormData:', displayTypeValue);
            
            Object.keys(formData).forEach(key => {
                if (key === 'schedule_mode') {
                    return;
                } else if (key === 'display_type') {
                    return;
                } else if (key === 'parent_id') {
                    // parent_id null olsa bile gönderilmeli (üst kategori kaldırma)
                    formDataToSend.append(key, formData[key] ?? '');
                } else if (key === 'active_days') {
                    if (formData[key] && Array.isArray(formData[key]) && formData[key].length > 0) {
                        formDataToSend.append(key, JSON.stringify(formData[key]));
                    }
                } else if (key === 'availability_schedule') {
                    if (formData[key] && typeof formData[key] === 'object') {
                        const hasActiveDay = Object.values(formData[key]).some(day => day && day.active);
                        if (hasActiveDay) {
                    formDataToSend.append(key, JSON.stringify(formData[key]));
                        }
                    }
                } else if (key === 'description') {
                    formDataToSend.append(key, formData[key] ?? '');
                } else if (key === 'time_based_active' || key === 'campaigns_menu_ignore_schedule') {
                    formDataToSend.append(key, formData[key] ? 'true' : 'false');
                } else if (formData[key] !== null && formData[key] !== '') {
                    formDataToSend.append(key, formData[key]);
                }
            });
            if (image) {
                formDataToSend.append('image', image);
            }

            const url = category
                ? `/organizations/${organizationId}/categories/${category.id}`
                : `/organizations/${organizationId}/categories`;

            // Debug: Log FormData contents
            console.log('🔍 FormData contents:', {
                display_type: formData.display_type,
                formDataKeys: Object.keys(formData),
                formDataToSendKeys: Array.from(formDataToSend.keys())
            });

            const response = await apiRequest(url, {
                method: category ? 'PUT' : 'POST',
                body: formDataToSend
            });

            console.log('🔍 Response data:', response.data);

            // If updating, pass the updated category data to onSuccess
            if (category && response.data) {
                onSuccess(response.data);
            } else {
                onSuccess();
            }
            onClose();
            resetForm();
        } catch (err) {
            setError(err.message || 'Kategori kaydedilirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                            {category ? 'Kategori Düzenle' : 'Yeni Kategori'}
                        </h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Image */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Görsel
                            </label>
                            <div className="flex items-center space-x-4">
                                {imagePreview && (
                                    <div className="relative">
                                        <img 
                                            src={imagePreview.startsWith('blob:') || imagePreview.startsWith('data:') ? imagePreview : getImageUrl(imagePreview)} 
                                            alt="Preview" 
                                            className="w-24 h-24 object-cover rounded-lg"
                                            onError={(e) => {
                                                console.error('Image preview error:', e);
                                                setError('Görsel önizlemesi yüklenemedi.');
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setImage(null);
                                                setImagePreview(null);
                                                const input = document.querySelector('input[type="file"]');
                                                if (input) input.value = '';
                                            }}
                                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 -mt-1 -mr-1"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                            </div>
                        </div>

                        {/* Parent Category */}
                        <div>
                            <label htmlFor="parent_id" className="block text-sm font-medium text-gray-700 mb-2">
                                Üst Kategori
                            </label>
                            <select
                                id="parent_id"
                                name="parent_id"
                                value={formData.parent_id || ''}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    parent_id: e.target.value ? parseInt(e.target.value) : null
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Ana Kategori (Üst kategori yok)</option>
                                {categoryOptions.map(opt => (
                                    <option key={opt.id} value={opt.id}>
                                        {'—'.repeat(opt.level)} {opt.name}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                                Bu kategoriyi başka bir kategorinin altında göstermek için üst kategori seçin.
                            </p>
                        </div>

                        {/* Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                Kategori Adı *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                                Açıklama
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows="3"
                                value={formData.description}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Sort Order */}
                        <div>
                            <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700 mb-2">
                                Sıra
                            </label>
                            <input
                                type="number"
                                id="sort_order"
                                name="sort_order"
                                value={formData.sort_order}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Display Type */}
                        <div>
                            <label htmlFor="display_type" className="block text-sm font-medium text-gray-700 mb-2">
                                Varsayılan Listeleme Görünümü
                            </label>
                            <select
                                id="display_type"
                                name="display_type"
                                value={formData.display_type}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="grid_single">Kare Tekli (1 sütun)</option>
                                <option value="grid_double">Kare İkili (2 sütun)</option>
                                <option value="list">Liste Görünümü</option>
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                                Kullanıcılar menü sayfasında bu görünümü varsayılan olarak görür. İsterseler görünümü değiştirebilirler.
                            </p>
                        </div>

                        {(() => {
                            const n = (formData.name || '').toLowerCase().trim();
                            if (n !== 'kampanyalar' && n !== 'kampanya') return null;
                            return (
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="campaigns_menu_ignore_schedule"
                                            checked={formData.campaigns_menu_ignore_schedule}
                                            onChange={handleChange}
                                            className="mt-1 w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                        />
                                        <span>
                                            <span className="block text-sm font-medium text-gray-900">Menüde saat gözetmeden kampanyaları göster</span>
                                            <span className="block text-xs text-gray-600 mt-1">
                                                Açıkken Kampanyalar bölümünde kampanyalar, tanımlı saatler dışında da (tarih aralığı içindeyken) listelenir. Kapalıyken yalnızca kampanyanın aktif saat/günlerinde bu bölümde görünür. Ürün kartlarındaki kampanya ve indirim vurguları her zaman saat kurallarına uyar.
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            );
                        })()}

                        {/* Date Range */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">📅 Tarih Aralığı</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                                        Başlangıç Tarihi
                                    </label>
                                    <input
                                        type="date"
                                        id="start_date"
                                        name="start_date"
                                        value={formData.start_date}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Kategori bu tarihten itibaren aktif olur</p>
                                </div>
                                <div>
                                    <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                                        Bitiş Tarihi
                                    </label>
                                    <input
                                        type="date"
                                        id="end_date"
                                        name="end_date"
                                        value={formData.end_date}
                                        onChange={handleChange}
                                        min={formData.start_date || undefined}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Kategori bu tarihe kadar aktif olur</p>
                                </div>
                            </div>
                        </div>

                        {/* Time Based Active */}
                        <div>
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    name="time_based_active"
                                    checked={formData.time_based_active}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                    ⏰ Saat bazlı aktif/pasif kontrolü
                                </span>
                            </label>
                        </div>

                        {/* Time Range */}
                        {formData.time_based_active && (
                            <>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Program Modu
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center space-x-2">
                                            <input
                                                type="radio"
                                                name="schedule_mode"
                                                value="simple"
                                                checked={formData.schedule_mode === 'simple'}
                                                onChange={(e) => setFormData(prev => ({ ...prev, schedule_mode: e.target.value }))}
                                                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700">Basit (Tüm günler için aynı saat)</span>
                                        </label>
                                        <label className="flex items-center space-x-2">
                                            <input
                                                type="radio"
                                                name="schedule_mode"
                                                value="advanced"
                                                checked={formData.schedule_mode === 'advanced'}
                                                onChange={(e) => setFormData(prev => ({ ...prev, schedule_mode: e.target.value }))}
                                                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700">Gelişmiş (Her gün için farklı saat)</span>
                                        </label>
                                    </div>
                                </div>

                                {formData.schedule_mode === 'simple' ? (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 mb-2">
                                            Başlangıç Saati
                                        </label>
                                        <input
                                            type="time"
                                            id="start_time"
                                            name="start_time"
                                            value={formData.start_time}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 mb-2">
                                            Bitiş Saati
                                        </label>
                                        <input
                                            type="time"
                                            id="end_time"
                                            name="end_time"
                                            value={formData.end_time}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

                                {/* Active Days */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Aktif Günler
                                    </label>
                                    <div className="grid grid-cols-4 gap-2">
                                        {days.map(day => (
                                            <label key={day.value} className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.active_days.includes(day.value)}
                                                    onChange={() => handleDayToggle(day.value)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-gray-700">{day.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                    </>
                                ) : (
                                    <div className="space-y-3">
                                        <p className="text-sm text-gray-600 mb-3">Her gün için farklı saat aralığı belirleyebilirsiniz:</p>
                                        {days.map(day => {
                                            const daySchedule = formData.availability_schedule?.[day.value] || { active: false, start: '', end: '' };
                                            return (
                                                <div key={day.value} className="border border-gray-200 rounded-lg p-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <label className="flex items-center space-x-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={daySchedule.active}
                                                                onChange={(e) => {
                                                                    const newSchedule = {
                                                                        ...(formData.availability_schedule || {}),
                                                                        [day.value]: {
                                                                            ...daySchedule,
                                                                            active: e.target.checked
                                                                        }
                                                                    };
                                                                    setFormData(prev => ({ ...prev, availability_schedule: newSchedule }));
                                                                }}
                                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                            />
                                                            <span className="text-sm font-medium text-gray-700">{day.label}</span>
                                                        </label>
                                                    </div>
                                                    {daySchedule.active && (
                                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                                            <input
                                                                type="time"
                                                                value={daySchedule.start || ''}
                                                                onChange={(e) => {
                                                                    const newSchedule = {
                                                                        ...(formData.availability_schedule || {}),
                                                                        [day.value]: {
                                                                            ...daySchedule,
                                                                            start: e.target.value
                                                                        }
                                                                    };
                                                                    setFormData(prev => ({ ...prev, availability_schedule: newSchedule }));
                                                                }}
                                                                placeholder="Başlangıç"
                                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            />
                                                            <input
                                                                type="time"
                                                                value={daySchedule.end || ''}
                                                                onChange={(e) => {
                                                                    const newSchedule = {
                                                                        ...(formData.availability_schedule || {}),
                                                                        [day.value]: {
                                                                            ...daySchedule,
                                                                            end: e.target.value
                                                                        }
                                                                    };
                                                                    setFormData(prev => ({ ...prev, availability_schedule: newSchedule }));
                                                                }}
                                                                placeholder="Bitiş"
                                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Status */}
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                                Durum
                            </label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="active">Aktif</option>
                                <option value="inactive">Pasif</option>
                            </select>
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                {loading ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CategoryFormModal;

